import { useCallback, useEffect, useRef, useState } from 'react'
import ListingDetailDefectPins, {
  isListingDefectPinTarget,
  type ListingDetailDefectPin,
} from './ListingDetailDefectPins'

const MIN_SCALE = 1
const MAX_SCALE = 3
const DOUBLE_TAP_SCALE = 2

type Props = {
  src: string
  alt?: string
  photoKey: string | number
  className?: string
  onZoomChange?: (zoomed: boolean) => void
  defectPins?: ListingDetailDefectPin[]
  allDefectMarks?: ListingDetailDefectPin[]
}

function touchDistance(touches: React.TouchList): number {
  if (touches.length < 2) return 0
  const a = touches.item(0)
  const b = touches.item(1)
  if (!a || !b) return 0
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

function touchMidpoint(touches: React.TouchList): { x: number; y: number } | null {
  if (touches.length < 2) return null
  const a = touches.item(0)
  const b = touches.item(1)
  if (!a || !b) return null
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 }
}

export default function LightboxZoomImage({
  src,
  alt = '',
  photoKey,
  className = '',
  onZoomChange,
  defectPins = [],
  allDefectMarks = [],
}: Props) {
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const scaleRef = useRef(1)
  const offsetRef = useRef({ x: 0, y: 0 })
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null)
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null)

  const clampOffset = useCallback((x: number, y: number, s: number) => {
    const el = containerRef.current
    if (!el || s <= 1) return { x: 0, y: 0 }
    const maxX = (el.clientWidth * (s - 1)) / 2
    const maxY = (el.clientHeight * (s - 1)) / 2
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    }
  }, [])

  const setTransform = useCallback(
    (nextScale: number, nextOffset: { x: number; y: number }) => {
      scaleRef.current = nextScale
      offsetRef.current = nextOffset
      setScale(nextScale)
      setOffset(nextOffset)
    },
    [],
  )

  const zoomTo = useCallback(
    (next: number, anchor?: { x: number; y: number }) => {
      const prev = scaleRef.current
      const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next))
      if (clamped <= 1.001) {
        setTransform(1, { x: 0, y: 0 })
        return
      }

      let { x, y } = offsetRef.current
      const el = containerRef.current
      if (anchor && el && prev > 0) {
        const rect = el.getBoundingClientRect()
        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        const factor = clamped / prev - 1
        x -= (anchor.x - cx) * factor
        y -= (anchor.y - cy) * factor
      }

      setTransform(clamped, clampOffset(x, y, clamped))
    },
    [clampOffset, setTransform],
  )

  const resetZoom = useCallback(() => {
    pinchRef.current = null
    dragRef.current = null
    setTransform(1, { x: 0, y: 0 })
  }, [setTransform])

  useEffect(() => {
    resetZoom()
  }, [photoKey, src, resetZoom])

  useEffect(() => {
    onZoomChange?.(scaleRef.current > 1.02)
  }, [scale, onZoomChange])

  function onWheel(e: React.WheelEvent) {
    e.preventDefault()
    e.stopPropagation()

    let delta = e.deltaY
    if (e.deltaMode === 1) delta *= 18
    else if (e.deltaMode === 2) delta *= 640

    const step = Math.max(-0.12, Math.min(0.12, -delta * 0.0012))
    zoomTo(scaleRef.current * (1 + step), { x: e.clientX, y: e.clientY })
  }

  function onDoubleClick(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (scaleRef.current > 1.05) {
      resetZoom()
      return
    }
    zoomTo(DOUBLE_TAP_SCALE, { x: e.clientX, y: e.clientY })
  }

  function onPointerDown(e: React.PointerEvent) {
    if (isListingDefectPinTarget(e.target)) return
    if (scaleRef.current <= 1 || pinchRef.current) return
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: offsetRef.current.x,
      oy: offsetRef.current.y,
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    const drag = dragRef.current
    if (!drag || pinchRef.current) return
    const x = drag.ox + (e.clientX - drag.x)
    const y = drag.oy + (e.clientY - drag.y)
    const next = clampOffset(x, y, scaleRef.current)
    setTransform(scaleRef.current, next)
  }

  function onPointerUp(e: React.PointerEvent) {
    if (!dragRef.current) return
    dragRef.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  function onTouchStart(e: React.TouchEvent) {
    if (e.touches.length === 2) {
      pinchRef.current = { dist: touchDistance(e.touches), scale: scaleRef.current }
      dragRef.current = null
    }
  }

  function onTouchMove(e: React.TouchEvent) {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault()
      const dist = touchDistance(e.touches)
      if (dist <= 0 || pinchRef.current.dist <= 0) return
      const anchor = touchMidpoint(e.touches)
      const ratio = dist / pinchRef.current.dist
      zoomTo(pinchRef.current.scale * ratio, anchor ?? undefined)
      pinchRef.current = { dist, scale: scaleRef.current }
      return
    }

    if (scaleRef.current > 1 && e.touches.length === 1 && !pinchRef.current) {
      const touch = e.touches[0]
      if (!touch) return
      if (!dragRef.current) {
        dragRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          ox: offsetRef.current.x,
          oy: offsetRef.current.y,
        }
        return
      }
      e.preventDefault()
      const x = dragRef.current.ox + (touch.clientX - dragRef.current.x)
      const y = dragRef.current.oy + (touch.clientY - dragRef.current.y)
      setTransform(scaleRef.current, clampOffset(x, y, scaleRef.current))
    }
  }

  function onTouchEnd(e: React.TouchEvent) {
    if (e.touches.length < 2) pinchRef.current = null
    if (e.touches.length === 0) dragRef.current = null
  }

  return (
    <div
      ref={containerRef}
      className={`lightbox-zoom${scale > 1.02 ? ' lightbox-zoom--zoomed' : ''}${className ? ` ${className}` : ''}`}
      onWheel={onWheel}
      onDoubleClick={onDoubleClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div
        className="lightbox-zoom__transform"
        style={{
          transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
        }}
      >
        <div className="lightbox-zoom__media">
          <img src={src} alt={alt} draggable={false} className="lightbox-zoom__img" />
          <ListingDetailDefectPins
            pins={defectPins}
            allMarks={allDefectMarks.length > 0 ? allDefectMarks : defectPins}
            popoverClassName="listing-detail-defect-popover--lightbox"
          />
        </div>
      </div>
    </div>
  )
}
